import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { User, Save, FileText } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user, token } = useAuth();
  const [f, setF] = useState({ name: '', phone: '', address: '', city: '', district: '', state: '', pincode: '', gstin: '', business_name: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/me`, { headers: { Authorization: `Bearer ${token}` } });
        setF({
          name: data.name || '',
          phone: data.phone || '',
          address: data.address || '',
          city: data.city || '',
          district: data.district || '',
          state: data.state || '',
          pincode: data.pincode || '',
          gstin: data.gstin || '',
          business_name: data.business_name || '',
        });
      } catch {}
    })();
  }, [token]);

  const save = async (e) => {
    e.preventDefault();
    if (f.gstin && f.gstin.trim().length !== 15) {
      toast({ title: 'GSTIN must be 15 characters' });
      return;
    }
    setBusy(true);
    try {
      await axios.patch(`${API}/me`, {
        name: f.name,
        phone: f.phone,
        address: f.address,
        city: f.city,
        district: f.district,
        state: f.state,
        pincode: f.pincode,
        gstin: f.gstin || null,
        business_name: f.business_name || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Profile updated', description: f.gstin ? 'Future invoices will auto-format as B2B.' : 'Profile saved.' });
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Try again' });
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <section className="py-12">
        <div className="max-w-2xl mx-auto px-6">
          <p className="eyebrow mb-2">Account</p>
          <h1 className="h-display text-3xl md:text-4xl">Your profile</h1>
          <p className="text-sm text-neutral-500 mt-1">Save your GSTIN once — future invoices auto-format as B2B tax invoices.</p>

          <form onSubmit={save} className="card-soft p-6 mt-8 space-y-4">
            <div className="flex items-center gap-3 pb-4 border-b">
              <div className="w-11 h-11 rounded-xl bg-navy text-white flex items-center justify-center"><User size={18} /></div>
              <div>
                <p className="font-semibold">{user?.name}</p>
                <p className="text-xs text-neutral-500">{user?.email} · {user?.role}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div><label className="text-xs">Full name</label><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" /></div>
              <div><label className="text-xs">Phone</label><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" /></div>
              <div className="md:col-span-2"><label className="text-xs">Address (for shipping & invoices)</label><textarea rows={2} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none resize-none" placeholder="Flat / house no, street, area" /></div>
              <div><label className="text-xs">City</label><input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" data-testid="profile-city-input" /></div>
              <div><label className="text-xs">District</label><input value={f.district} onChange={(e) => setF({ ...f, district: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" data-testid="profile-district-input" /></div>
              <div><label className="text-xs">State</label><input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" data-testid="profile-state-input" /></div>
              <div><label className="text-xs">PIN code</label><input value={f.pincode} onChange={(e) => setF({ ...f, pincode: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" data-testid="profile-pincode-input" maxLength={6} /></div>
              <div><label className="text-xs">Business name (optional)</label><input value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" /></div>
            </div>

            <div className="p-4 rounded-xl bg-navy/5 border border-navy/10">
              <div className="flex items-center gap-2 text-navy">
                <FileText size={16} />
                <p className="font-semibold text-sm">GSTIN — for B2B invoices</p>
              </div>
              <p className="text-xs text-neutral-600 mt-1">Save your 15-character GSTIN to receive tax invoices in B2B format (CGST/SGST split, GSTIN visible, place of supply).</p>
              <input value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value.toUpperCase() })} maxLength={15} className="mt-3 w-full px-3 py-2.5 rounded-lg border border-black/15 uppercase tracking-wider font-mono focus:ring-2 focus:ring-navy/30 focus:outline-none" placeholder="29ABCDE1234F1Z5" />
            </div>

            <button disabled={busy} type="submit" className="btn-primary w-full justify-center disabled:opacity-60"><Save size={14} /> {busy ? 'Saving…' : 'Save profile'}</button>
          </form>
        </div>
      </section>
    </Layout>
  );
}
